import { useState } from "react";
import { Plane, Building2, Star, Check, Heart, Ban, FolderInput, Scissors } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import SaveTripDialog from "../SaveTripDialog";

function fmtTime(iso) { return iso ? iso.slice(11, 16) : "—"; }
function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtMoney(amount, currency) {
  if (!amount) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(parseFloat(amount));
  } catch { return `${amount} ${currency || ""}`; }
}
function fmtDur(iso) {
  if (!iso) return "";
  const m = iso.match(/P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?/);
  if (!m) return iso;
  const hours = (parseInt(m[1] || 0, 10) * 24) + parseInt(m[2] || 0, 10);
  return [hours ? `${hours}h` : null, m[3] ? `${m[3]}m` : null].filter(Boolean).join(" ") || iso;
}

function OfferRow({ offer }) {
  const [dialog, setDialog] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <div className="rounded-card border border-hairline bg-surface p-3 flex items-center gap-3" data-testid="chat-offer-row">
      {offer.airline_logo ? (
        <img src={offer.airline_logo} alt={offer.airline} className="w-6 h-6 object-contain shrink-0" />
      ) : (
        <Plane size={16} className="text-sand-400 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">
          {offer.airline}
          {offer.source === "google" && (
            <span className="ml-2 font-mono text-[9px] uppercase tracking-wide text-sand-500 border border-sand-300 px-1 py-px" data-testid="chat-offer-google-badge">Google</span>
          )}
        </p>
        {offer.slices.map((s, i) => (
          <p key={i} className="text-[11px] font-mono text-sand-600 truncate">
            {s.origin} {fmtTime(s.departing_at)} → {s.destination} {fmtTime(s.arriving_at)}
            <span className="text-sand-400"> · {fmtDay(s.departing_at)} · {s.stops === 0 ? "nonstop" : `${s.stops} stop`} · {fmtDur(s.duration)}</span>
          </p>
        ))}
      </div>
      <div className="shrink-0 text-right">
        <p className="font-display font-medium text-base">{fmtMoney(offer.total_amount, offer.total_currency)}</p>
        {saved ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-700" data-testid="offer-saved-label">
            <Check size={10} /> Saved
          </span>
        ) : (
          <button onClick={() => setDialog(true)} className="text-[10px] font-mono uppercase tracking-wide text-ember hover:underline"
            data-testid="offer-save-btn">
            + Save to trip
          </button>
        )}
      </div>
      {dialog && <SaveTripDialog offer={offer} onClose={(ok) => { setDialog(false); if (ok) setSaved(true); }} />}
    </div>
  );
}

function StayRow({ stay }) {
  const [dialog, setDialog] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <div className="rounded-card border border-hairline bg-surface p-3 flex items-center gap-3" data-testid="chat-stay-row">
      {stay.photo ? (
        <img src={stay.photo} alt={stay.name} className="w-12 h-12 object-cover shrink-0" />
      ) : (
        <div className="w-12 h-12 bg-sand-100 flex items-center justify-center shrink-0"><Building2 size={16} className="text-sand-300" /></div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{stay.name}</p>
        <p className="text-[11px] text-sand-500 truncate">
          {stay.city}{stay.rating ? <span className="inline-flex items-center gap-0.5 ml-2"><Star size={9} className="fill-amber-400 text-amber-400" />{stay.rating}</span> : null}
          {stay.review_score ? ` · ${stay.review_score}/10` : ""}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-display font-medium text-base">{fmtMoney(stay.total_amount, stay.currency)}</p>
        {saved ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-700" data-testid="stay-saved-label">
            <Check size={10} /> Saved
          </span>
        ) : (
          <button onClick={() => setDialog(true)} className="text-[10px] font-mono uppercase tracking-wide text-ember hover:underline"
            data-testid="stay-save-btn">
            + Save to trip
          </button>
        )}
      </div>
      {dialog && <SaveTripDialog stay={stay} onClose={(ok) => { setDialog(false); if (ok) setSaved(true); }} />}
    </div>
  );
}

function whenLabel(p) {
  if (p.season) return p.season;
  if (p.date_from && p.date_to) return `${p.date_from} – ${p.date_to}`;
  if (p.date_from) return p.date_from;
  return "anytime";
}

function ProposalCard({ proposal, index, messageId, onAction }) {
  const p = proposal;
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try { await onAction(index, action); } finally { setBusy(false); }
  };
  return (
    <div className={`border p-4 ${p.approved ? "border-emerald-300 bg-emerald-50" : p.dismissed ? "border-sand-200 bg-surface opacity-50" : "border-ember bg-ember/[0.03]"}`}
      data-testid={`wishlist-proposal-${index}`}>
      <p className="label-tag mb-1.5 text-ember flex items-center gap-1.5"><Heart size={11} /> Wishlist proposal</p>
      <p className="text-sm font-medium">{p.title}</p>
      <p className="text-[11px] text-sand-500 font-mono mt-0.5">
        {[p.type, [p.location, p.city, p.country].filter(Boolean).join(", "), whenLabel(p), p.priority?.replace("_", " ")].filter(Boolean).join(" · ")}
      </p>
      {p.notes && <p className="text-xs text-sand-600 mt-1">{p.notes}</p>}
      <div className="mt-3">
        {p.approved ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-mono" data-testid={`proposal-approved-${index}`}><Check size={12} /> Added to wishlist</span>
        ) : p.dismissed ? (
          <span className="text-xs text-sand-500 font-mono">Dismissed</span>
        ) : (
          <div className="flex gap-2">
            <button disabled={!messageId || busy} onClick={() => act("approve")}
              className="btn-swiss btn-primary text-xs disabled:opacity-50" data-testid={`proposal-approve-${index}`}>
              <Check size={12} className="mr-1" /> Approve
            </button>
            <button disabled={!messageId || busy} onClick={() => act("dismiss")}
              className="btn-swiss btn-ghost text-xs disabled:opacity-50" data-testid={`proposal-dismiss-${index}`}>
              Dismiss
            </button>
            {!messageId && <span className="text-[10px] text-sand-400 self-center">available when the reply finishes</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function summaryLabel(s) {
  if (!s) return "Reservation";
  if (s.type === "flight") return [s.airline, s.flight_number, s.from && s.to ? `${s.from} → ${s.to}` : null].filter(Boolean).join(" · ") || "Flight";
  if (s.type === "hotel") return s.name || "Hotel";
  if (s.type === "car") return s.vendor ? `${s.vendor} car rental` : "Car rental";
  if (s.type === "event") return s.name || "Event";
  return "Reservation";
}

function CancellationCard({ proposal, index, messageId, onAction }) {
  const p = proposal;
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try { await onAction(index, action); } finally { setBusy(false); }
  };
  return (
    <div className={`border p-4 ${p.confirmed ? "border-red-300 bg-red-50" : p.dismissed ? "border-sand-200 bg-surface opacity-50" : "border-error bg-red-50/60"}`}
      data-testid={`cancel-proposal-${index}`}>
      <p className="label-tag mb-1.5 text-error flex items-center gap-1.5"><Ban size={11} /> Cancel this reservation?</p>
      <p className="text-sm font-medium">{summaryLabel(p.summary)}</p>
      {p.trip_name && <p className="text-[11px] text-sand-500 font-mono mt-0.5">Trip · {p.trip_name}</p>}
      {p.reason && <p className="text-xs text-sand-600 mt-1">{p.reason}</p>}
      <div className="mt-3">
        {p.confirmed ? (
          <span className="inline-flex items-center gap-1 text-xs text-error font-mono" data-testid={`cancel-confirmed-${index}`}><Check size={12} /> Marked cancelled</span>
        ) : p.dismissed ? (
          <span className="text-xs text-sand-500 font-mono">Kept — not cancelled</span>
        ) : (
          <div className="flex gap-2">
            <button disabled={!messageId || busy} onClick={() => act("approve")}
              className="btn-swiss text-xs disabled:opacity-50 bg-error text-white hover:bg-red-600" data-testid={`cancel-approve-${index}`}>
              <Ban size={12} className="mr-1" /> Confirm cancel
            </button>
            <button disabled={!messageId || busy} onClick={() => act("dismiss")}
              className="btn-swiss btn-ghost text-xs disabled:opacity-50" data-testid={`cancel-dismiss-${index}`}>
              Keep it
            </button>
            {!messageId && <span className="text-[10px] text-sand-400 self-center">available when the reply finishes</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({ proposal, index, messageId, onAction }) {
  const p = proposal;
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try { await onAction(index, action); } finally { setBusy(false); }
  };
  const isMove = p.action === "move";
  return (
    <div className={`border p-4 ${p.confirmed ? "border-emerald-300 bg-emerald-50" : p.dismissed ? "border-sand-200 bg-surface opacity-50" : "border-amber-400 bg-amber-50/60"}`}
      data-testid={`action-proposal-${index}`}>
      <p className="label-tag mb-1.5 text-amber-700 flex items-center gap-1.5">
        {isMove ? <FolderInput size={11} /> : <Scissors size={11} />} {isMove ? "Move this reservation?" : "Split this trip?"}
      </p>
      {isMove ? (
        <>
          <p className="text-sm font-medium">{summaryLabel(p.summary)}</p>
          <p className="text-[11px] text-sand-500 font-mono mt-0.5">{p.from_trip_name || "Trip"} → {p.to_trip_name || "Trip"}</p>
        </>
      ) : (
        <>
          <p className="text-sm font-medium">{p.trip_name || "Trip"}</p>
          <p className="text-[11px] text-sand-500 font-mono mt-0.5">
            {p.split_date ? `Reservations on/after ${p.split_date} move to the new trip` : `${(p.reservation_ids || []).length} reservation(s) move to the new trip`}
            {p.new_trip_name ? ` · “${p.new_trip_name}”` : ""}
          </p>
        </>
      )}
      <div className="mt-3">
        {p.confirmed ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-mono" data-testid={`action-confirmed-${index}`}><Check size={12} /> {isMove ? "Moved" : "Split"}</span>
        ) : p.dismissed ? (
          <span className="text-xs text-sand-500 font-mono">Dismissed — nothing changed</span>
        ) : (
          <div className="flex gap-2">
            <button disabled={!messageId || busy} onClick={() => act("approve")}
              className="btn-swiss btn-primary text-xs disabled:opacity-50" data-testid={`action-approve-${index}`}>
              <Check size={12} className="mr-1" /> Confirm {isMove ? "move" : "split"}
            </button>
            <button disabled={!messageId || busy} onClick={() => act("dismiss")}
              className="btn-swiss btn-ghost text-xs disabled:opacity-50" data-testid={`action-dismiss-${index}`}>
              Dismiss
            </button>
            {!messageId && <span className="text-[10px] text-sand-400 self-center">available when the reply finishes</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

function SourcesRow({ sources }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="chat-sources">
      <span className="font-mono text-[10px] uppercase tracking-wide text-sand-400">Sources</span>
      {sources.slice(0, 6).map((s, i) => (
        <a key={i} href={s.url} target="_blank" rel="noreferrer" title={s.title || s.url}
          className="px-2 py-0.5 border border-hairline bg-surface text-[11px] text-dusk hover:border-dusk truncate max-w-[220px]"
          data-testid={`chat-source-${i}`}>
          {hostOf(s.url)}
        </a>
      ))}
    </div>
  );
}

export default function ChatArtifacts({ artifacts, messageId, onProposalAction, onCancelAction, onActionAction }) {
  const offers = artifacts?.flight_offers || [];
  const stays = artifacts?.stays || [];
  const proposals = artifacts?.wishlist_proposals || [];
  const cancels = artifacts?.cancellation_proposals || [];
  const actions = artifacts?.action_proposals || [];
  const sources = artifacts?.sources || [];
  if (offers.length === 0 && stays.length === 0 && proposals.length === 0 && cancels.length === 0 && actions.length === 0 && sources.length === 0) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="chat-artifacts">
      {cancels.map((p, i) => (
        <CancellationCard key={i} proposal={p} index={i} messageId={messageId} onAction={onCancelAction} />
      ))}
      {actions.map((p, i) => (
        <ActionCard key={i} proposal={p} index={i} messageId={messageId} onAction={onActionAction} />
      ))}
      {proposals.map((p, i) => (
        <ProposalCard key={i} proposal={p} index={i} messageId={messageId} onAction={onProposalAction} />
      ))}
      {offers.slice(0, 5).map((o, i) => <OfferRow key={o.id || i} offer={o} />)}
      {stays.slice(0, 5).map((s, i) => <StayRow key={s.id || i} stay={s} />)}
      {sources.length > 0 && <SourcesRow sources={sources} />}
    </div>
  );
}
